import { yupResolver } from '@hookform/resolvers/yup'
import { ArticleOutlined } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { FunctionComponent, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { MyDialogTitle } from '../../../../base/components/Dialog/MyDialogTitle.tsx'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import {
  TIPO_ARTICULO_INITIAL_VALUES,
  TipoArticuloInputProps,
  TipoArticuloProps,
} from '../../../../interfaces/tipoArticulo.ts'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiTipoArticuloRegistro } from '../../api/apiTipoArticuloRegistrado'
import { TipoArticuloApiInputProps } from '../../interfaces'
import { tipoArticuloComposeService } from '../../services/tipoArticuloRegistroComposeService'
import { TipoArticuloValidationSchema } from '../../validator/tipoArticuloValidator.ts'
import TipoArticuloForm from '../TipoArticuloForm'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: TipoArticuloProps) => void
}

type Props = OwnProps

/**
 * Formulario para upsert de tipos de articulo
 * @param props
 * @constructor
 */
const TipoArticuloRegistroDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, ...other } = props

  const form = useForm<TipoArticuloInputProps>({
    defaultValues: { ...TIPO_ARTICULO_INITIAL_VALUES },
    resolver: yupResolver<any, any, any>(TipoArticuloValidationSchema),
  })
  const { requestConfirm, showError, toast } = useUtils()

  const { mutate, isPending } = useMutation({
    mutationFn: (data: TipoArticuloApiInputProps) => apiTipoArticuloRegistro(data),
    onMutate: () => {
      swalLoading()
    },
    onSuccess: (res) => {
      if (res) {
        toast.success('Registro exitoso')
        onClose(res)
      }
    },
    onError: (err) => {
      showError(err)
    },
    onSettled: () => {
      swalClose()
    },
  })

  /**
   * Guardamos los datos
   * @param values
   */
  const onSubmit: SubmitHandler<TipoArticuloInputProps> = useCallback(
    async (values) => {
      const apiInput = tipoArticuloComposeService(values)

      const { confirmed } = await requestConfirm()
      if (!confirmed) return
      mutate(apiInput)
    },
    [requestConfirm, mutate],
  )

  const onError = useCallback((errors: any, e: any) => console.log(errors, e), [])

  /** Funcion de limpieza total */
  const handleExited = useCallback(() => {
    form.reset(TIPO_ARTICULO_INITIAL_VALUES)
  }, [form])

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '100%', maxHeight: '80vh' } }}
      maxWidth="sm"
      open={open}
      slotProps={{
        transition: {
          onExited: handleExited,
        },
      }}
      {...other}
    >
      <MyDialogTitle icon={ArticleOutlined}>Nuevo tipo articulo</MyDialogTitle>
      <DialogContent dividers>
        <TipoArticuloForm form={form} handleEdit={false} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button color={'error'} onClick={() => onClose()} disabled={isPending}>
          Cerrar
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit, onError)}
          variant={'contained'}
          size="small"
          disabled={isPending}
        >
          Registrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TipoArticuloRegistroDialog
